import {
	AccordionDetails,
	AccordionSummary,
	Button,
	LinearProgress,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import {
	BusinessDataContext,
	BusinessDataContextValues,
	MultistepContext,
	MultistepContextValues,
} from "../../../types/context";
import { useIntl } from "react-intl";
import { LangContext } from "../../../types/internationalization";
import { dateToString, formatTime } from "../../../utils/dateFormat";
import { theme } from "../../../styles/themeProvider";
import {
	DetailAccordion,
	DetailTypography,
} from "../../styled/detailsComponents";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	DetailItemStack,
	DetailText,
	LanguageSelect,
	NumberInput,
	RadioButtonGroup,
} from "./detailsFormComponents";
import { ReservationApi } from "../../../api/reservationApi";
import { PaymentApi } from "../../../api/paymentApi";
import { ErrorTypography } from "../../styled/formComponents";
import { MercadoPagoPayment } from "./MercadoPagoPayment";
import {
	emptyPayment,
	PaymentResponse,
	Reservation,
} from "../../../types/entities";
import ReservationAlert from "./ReservationAlert";

const languages: Record<string, string> = {
	"es-ES": "Español",
	"en-US": "Inglés",
};

const Details = ({ setSuccess }) => {
	const { formatMessage } = useIntl();

	const { reservation, onSubmit, reservationMade } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const langContext = useContext(LangContext);
	const locale = langContext!.locale;

	const reservationRoom =
		reservation.note === null ||
		reservation.note.indexOf("Preferencia de espacio: ") === -1
			? null
			: reservation.note.split("Preferencia de espacio: ")[1].split(" ")[0];

	const reservationLanguage =
		reservation.note === null ||
		reservation.note.indexOf("Preferencia de idioma: ") === -1
			? null
			: reservation.note.split("Preferencia de idioma: ")[1].split(" ")[0];

	const reservationNotes =
		reservation.note === null ||
		reservation.note.indexOf(" | Preferencia ") === -1
			? ""
			: reservation.note.substring(0, reservation.note.indexOf(" | "));

	const [language, setLanguage] = useState(reservationLanguage);
	const [numberCeliac, setNumberCeliac] = useState(reservation.numberCeliac);
	const [numberKids, setNumberKids] = useState(reservation.numberKids);
	const [pets, setPets] = useState(reservation.pets);
	const [room, setRoom] = useState(reservationRoom);
	const [notes, setNotes] = useState(reservationNotes);

	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	const [requiresFee, setRequiresFee] = useState(false);
	const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
	const [preferences, setPreferences] = useState<PaymentResponse | null>(null);

	const paymentApi = new PaymentApi();

	useEffect(() => {
		const getStatus = async () => {
			setLoading(true);
			const response = await paymentApi.getPaymentStatus(reservation.paymentId!);
			const status = response.data;
			setPaymentStatus(status);
			setLoading(false);
			//If the reservation is made and the payment status is not approved, we have to get the preferences
			if (
				reservationMade &&
				(status === "rejected" ||
					status === "cancelled" ||
					status === "not_found")
			) {
				getPreferences(reservation);
			}
		};

		if (reservation.id !== null) {
			//If we have a reservation and requires fee, we set requiresFee into true
			if (reservation.service && reservation.service.category.bookingFee > 0) {
				setRequiresFee(true);
				if (reservation.state?.name === "PENDING") getStatus();
				else setPaymentStatus("approved");
			}
		}
	}, []);

	const handleSubmit = async () => {
		try {
			setError("");
			setLoading(true);
			let reservationNotes = notes;
			if (language)
				reservationNotes +=
					(reservationNotes !== "" ? " | " : "") +
					"Preferencia de idioma: " +
					languages[language];
			if (room)
				reservationNotes +=
					(reservationNotes !== "" ? " | " : "") +
					"Preferencia de espacio: " +
					room;

			let service = reservation.service;

			let newReservation: Reservation = {
				...reservation,
				numberCeliac,
				numberKids,
				pets,
				service,
				note: reservationNotes,
				bookingFee: reservation.service!.category.bookingFee * reservation.numberDiners,
			};

			let auxReservation;
			if (reservation.service!.category.bookingFee > 0) {
				setRequiresFee(true);
				auxReservation = await getPreferences(newReservation);
				setPaymentStatus("not_generated");
			} else {
				const reservationApi = new ReservationApi();
				auxReservation = await reservationApi.createReservation(newReservation);
				setPaymentStatus("not_required");
			}
			newReservation.id = auxReservation.id!;
			setSuccess(
				formatMessage({
					id: "details.success",
					defaultMessage: "Reserva creada con éxito!",
				})
			);
			onSubmit(newReservation);
			setLoading(false);
		} catch (error) {
			setError(
				formatMessage({
					id: "details.error",
					defaultMessage: "Ocurrió un error al crear la reserva",
				})
			);
			setLoading(false);
		}
	};

	const getPreferences = async (res: Reservation) => {
		try {
			// I get the payment preferences by sending the title and unit price, then I set
			// the external reference of the reservation as the reservation paymentId
			let preferences = {
				...emptyPayment,
				title: reservation.service!.name,
				unitPrice: reservation.service!.category.bookingFee,
				quantity: reservation.numberDiners,
			};
			const response = await paymentApi.fetchPaymentPreferences(preferences);
			setPreferences(response.data);
			res.paymentId = response.data.externalReference;

			//Now I save the reservation with that paymentId
			const reservationApi = new ReservationApi();
			const reservationResponse = await reservationApi.createReservation(res);
			reservation.paymentId = reservationResponse.data.paymentId!;
			return reservationResponse.data;
		} catch (error) {
			setError(
				formatMessage({
					id: "details.preferencesError",
					defaultMessage: "Ocurrió un error al generar el pago.",
				})
			);
			setLoading(false);
		}
	};

	//If the payment is successful, we will call to our API to set the reservation status as "CONFIRMED"
	const updateReservationPayment = () => {
		const payReservation = async () => {
			try {
				await paymentApi.getPaymentStatus(reservation.paymentId!);
				setPreferences(null);
			} catch (error) {
				setError(
					formatMessage({
						id: "details.preferencesError",
						defaultMessage: "Ocurrió un error al generar el pago.",
					})
				);
			}
		};
		payReservation();
	};

	return (
		<Stack width="100%" paddingBottom={2} spacing={2}>
			<Stack
				width="100%"
				direction="row"
				justifyContent="space-around"
				sx={{
					backgroundColor: theme.palette.secondary.light,
					borderRadius: "10px",
					paddingY: "12px",
				}}
			>
				<DetailTypography>
					{`${reservation.numberDiners} ${formatMessage({
						id: "details.diner",
						defaultMessage: "persona",
					})}${reservation.numberDiners > 1 ? "s" : ""}`}
				</DetailTypography>
				<DetailTypography>
					{dateToString(reservation.time!, locale)}
				</DetailTypography>
				<DetailTypography>
					{formatTime(
						reservation.time!.toLocaleTimeString("es-ES", {
							hour: "2-digit",
							minute: "2-digit",
						}),
						locale
					)}
				</DetailTypography>
			</Stack>
			<DetailAccordion>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography>
						<span style={{ fontWeight: 700 }}>
							{formatMessage({
								id: "details.preferences",
								defaultMessage: "Preferencias",
							})}
						</span>{" "}
						{formatMessage({
							id: "details.optional",
							defaultMessage: "(Opcionales)",
						})}
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{ padding: "20px 10px" }}>
					<Stack alignItems="center" spacing={{ mobile: 3, sm: 4 }}>
						<Stack
							width="100%"
							direction={{ mobile: "column", sm: "row" }}
							spacing={{ mobile: 3, sm: 0 }}
							alignItems="flex-end"
						>
							<DetailItemStack>
								<DetailText
									name="language"
									defaultMessage="Preferencia de idioma"
								/>
								<LanguageSelect
									name="language"
									defaultValue={language ?? locale}
									onChange={(newValue) => setLanguage(newValue)}
								/>
							</DetailItemStack>
							<Stack
								width="100%"
								direction={{ mobile: "column", xs: "row" }}
								spacing={{ mobile: 3, xs: 0 }}
								alignItems="flex-end"
								flex={2}
							>
								<DetailItemStack>
									<DetailText name="celiacs" defaultMessage="¿Algún celíaco?" />
									<NumberInput
										name="celiacs"
										defaultValue={numberCeliac}
										onChange={(newValue) => setNumberCeliac(newValue)}
									/>
								</DetailItemStack>
								<DetailItemStack>
									<DetailText name="kids" defaultMessage="¿Venís con niños?" />
									<NumberInput
										name="kids"
										defaultValue={numberKids}
										onChange={(newValue) => setNumberKids(newValue)}
									/>
								</DetailItemStack>
							</Stack>
						</Stack>
						<Stack
							width="100%"
							direction={{ mobile: "column", sm: "row" }}
							alignItems="flex-start"
							spacing={{ mobile: 3, sm: 0 }}
						>
							<DetailItemStack>
								<DetailText
									name="room"
									defaultMessage="¿Alguna preferencia de ambiente?"
								/>
								<RadioButtonGroup
									name="room"
									defaultValue={room ?? ""}
									options={[
										{ value: "Interior", name: "indoor" },
										{ value: "Galería", name: "porche" },
										{ value: "Jardín", name: "garden" },
									]}
									onChange={(newValue) => setRoom(newValue)}
								/>
							</DetailItemStack>
							<DetailItemStack>
								<DetailText
									name="pets"
									defaultMessage="¿Venís con tu mascota?"
								/>
								<RadioButtonGroup
									name="pets"
									defaultValue={pets ? "true" : "false"}
									options={[
										{ value: "true", name: "true" },
										{ value: "false", name: "false" },
									]}
									onChange={(newValue) => setPets(newValue)}
								/>
							</DetailItemStack>
						</Stack>
						<DetailItemStack>
							<DetailText
								name="notes"
								defaultMessage="¿Algo más que debamos saber?"
							/>
							<TextField
								multiline
								rows={3}
								value={notes}
								disabled={reservationMade}
								placeholder={formatMessage({
									id: "details.notes.placeholder",
									defaultMessage: "Dejanos tus comentarios acá...",
								})}
								onChange={(event) => setNotes(event.target.value)}
								sx={{ width: "100%" }}
							/>
						</DetailItemStack>
					</Stack>
				</AccordionDetails>
			</DetailAccordion>
			{error && <ErrorTypography>{error}</ErrorTypography>}
			{reservationMade && paymentStatus && (
				<ReservationAlert
					requiresFee={requiresFee}
					paymentStatus={paymentStatus}
					fee={
						reservation.service?.category.bookingFee
							? reservation.service!.category.bookingFee *
							  reservation.numberDiners
							: 0
					}
				/>
			)}
			{reservationMade && requiresFee && paymentStatus !== "approved" && (
				<Typography>{reservation.service?.category.message}</Typography>
			)}
			{loading && <LinearProgress />}
			{!reservationMade && (
				<Button variant="contained" onClick={handleSubmit} sx={{ height: 50 }}>
					{formatMessage({
						id: "details.book",
						defaultMessage: "Reservar",
					})}
				</Button>
			)}
			{reservationMade && preferences && (
				<MercadoPagoPayment
					preferences={preferences}
					updateReservationPayment={updateReservationPayment}
					setPaymentStatus={setPaymentStatus}
				/>
			)}
		</Stack>
	);
};

export default Details;
